import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import {
  buildAudienceCompositionSegments,
  type AudienceCompositionSegment,
} from '@/components/audiences/build-audience-composition-segments';
import { cn } from '@/lib/utils';

interface TooltipPayloadItem {
  payload: AudienceCompositionSegment;
}

function CompositionTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}) {
  if (!active || !payload?.length) return null;
  const segment = payload[0]?.payload;
  if (!segment) return null;
  return (
    <div className="rounded-lg border border-input bg-background px-2 py-1.5 text-xs shadow-md">
      <p className="flex items-center gap-1.5 font-medium text-foreground">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: segment.color }}
        />
        {segment.label}
      </p>
      <p className="text-muted-foreground">
        {segment.actualValue.toLocaleString()}
      </p>
    </div>
  );
}

interface AudienceFilterDonutProps {
  targetUsersCount?: number;
  totalUserbaseCount: number;
  locale: Locales;
  isLoading?: boolean;
  className?: string;
}

export function AudienceFilterDonut({
  targetUsersCount,
  totalUserbaseCount,
  locale,
  isLoading = false,
  className,
}: AudienceFilterDonutProps) {
  const { LL } = useI18nContext();
  const isEmpty = targetUsersCount === undefined;
  const resolvedCount = targetUsersCount ?? 0;

  const data = useMemo(
    () =>
      buildAudienceCompositionSegments({
        targetUsersCount,
        totalUserbaseCount,
        labels: {
          usersInAudience: LL.audiences.details.overview.usersInAudience(),
          otherUsers: LL.audiences.details.overview.otherUsers(),
        },
      }),
    [targetUsersCount, totalUserbaseCount, LL.audiences.details.overview]
  );

  const hasAnyData = !isEmpty && totalUserbaseCount > 0;
  const formattedCount = isEmpty ? '—' : resolvedCount.toLocaleString(locale);
  const formattedTotal = totalUserbaseCount.toLocaleString(locale);

  const getFontSize = () => {
    const length = formattedCount.length;
    if (length <= 4) return 'text-4xl';
    if (length <= 6) return 'text-2xl';
    return 'text-lg';
  };

  return (
    <div
      className={cn(
        'flex shrink-0 flex-col items-center gap-3 sm:pt-1',
        className
      )}
    >
      <div className="relative h-60 w-60">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius="85%"
              outerRadius="100%"
              startAngle={90}
              endAngle={-270}
              paddingAngle={hasAnyData && data.length > 1 ? 3 : 0}
              strokeWidth={0}
              isAnimationActive
              animationDuration={500}
              animationBegin={200}
              animationEasing="ease-in-out"
              tabIndex={-1}
              style={{ outline: 'none' }}
            >
              {data.map((entry) => (
                <Cell
                  key={entry.key}
                  fill={entry.color}
                  tabIndex={-1}
                  style={{ outline: 'none' }}
                />
              ))}
            </Pie>
            {hasAnyData ? (
              <Tooltip
                content={<CompositionTooltip />}
                cursor={false}
                wrapperStyle={{ outline: 'none', zIndex: 50 }}
              />
            ) : null}
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center entry-fade-in-up entry-delay-1">
          {isLoading ? (
            <Skeleton className="mb-1 h-9 w-20" />
          ) : (
            <p
              className={cn(
                'font-semibold tabular-nums tracking-tight text-foreground',
                getFontSize(),
                isEmpty && 'text-muted-foreground'
              )}
            >
              {formattedCount}
            </p>
          )}

          <p className="max-w-[10rem] text-center text-sm text-muted-foreground">
            {LL.audiences.details.overview.filterComposition.targetCustomersLabel()}
          </p>
          {totalUserbaseCount > 0 && (
            <p className="text-xs tabular-nums text-muted-foreground">
              {LL.audiences.details.overview.filterComposition.ofTotal({
                total: formattedTotal,
              })}
            </p>
          )}
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground mt-2 entry-fade-in entry-delay-1 max-w-60">
        {LL.audiences.details.overview.filterComposition.description()}
      </p>
    </div>
  );
}
